import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  Stack
} from '@mui/material';
import {
  QrCode2 as QrCodeIcon,
  Login as LoginIcon,
  PersonAdd as SignupIcon,
  Analytics as AnalyticsIcon,
  Edit as EditIcon,
  Security as SecurityIcon
} from '@mui/icons-material';

const Home: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        py: 4
      }}
    >
      <Container maxWidth="lg">
        <Paper
          elevation={24}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}
        >
          {/* Header */}
          <Box textAlign="center" mb={6}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 3
              }}
            >
              <img
                src="/logo-numih.png"
                alt="Numihfrance Logo"
                style={{
                  height: '120px',
                  width: 'auto',
                }}
              />
            </Box>
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Numihfrance QRcode
            </Typography>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              Créez et gérez vos QR codes dynamiques
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2, maxWidth: 600, mx: 'auto' }}>
              Une plateforme professionnelle pour créer, suivre et analyser vos QR codes.
              Modifiez les destinations à tout moment sans regénérer vos codes.
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            mb={6}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<LoginIcon />}
              onClick={() => navigate('/login')}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: 6
                },
                transition: 'all 0.3s'
              }}
            >
              Se connecter
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<SignupIcon />}
              onClick={() => navigate('/signup')}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem',
                borderWidth: 2,
                borderColor: '#667eea',
                color: '#667eea',
                '&:hover': {
                  borderWidth: 2,
                  borderColor: '#5568d3',
                  background: 'rgba(102, 126, 234, 0.05)',
                  transform: 'translateY(-2px)',
                  boxShadow: 3
                },
                transition: 'all 0.3s'
              }}
            >
              Créer un compte
            </Button>
          </Stack>

          {/* Features */}
          <Typography variant="h4" textAlign="center" gutterBottom sx={{ mb: 4, fontWeight: 600 }}>
            Fonctionnalités
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 8
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    mb: 2
                  }}
                >
                  <QrCodeIcon sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  QR Codes Dynamiques
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Créez des QR codes personnalisés avec nom, description et destination modifiable.
                  Changez l'URL à tout moment sans regénérer le code.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 8
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    mb: 2
                  }}
                >
                  <AnalyticsIcon sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Statistiques Détaillées
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Suivez les performances de vos QR codes avec des statistiques en temps réel.
                  Nombre de scans, dernière utilisation et plus encore.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 8
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    mb: 2
                  }}
                >
                  <EditIcon sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Gestion Facile
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Interface intuitive pour créer, modifier et supprimer vos QR codes.
                  Téléchargez vos codes aux formats PNG ou SVG.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 8
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    mb: 2
                  }}
                >
                  <SecurityIcon sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Sécurisé et Fiable
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Vos données sont protégées avec des systèmes de sécurité avancés.
                  Validation des comptes et accès contrôlé par administrateur.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 8
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    mb: 2
                  }}
                >
                  <SignupIcon sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Comptes Personnalisés
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Chaque utilisateur possède son propre espace avec ses QR codes.
                  Inscription simple avec validation par email et approbation admin.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 8
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    mb: 2
                  }}
                >
                  <QrCodeIcon sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Shortcodes Uniques
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Chaque QR code dispose d'un shortcode unique pour une redirection rapide.
                  URLs courtes et mémorables pour vos campagnes.
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Footer CTA */}
          <Box textAlign="center" mt={6} pt={4} borderTop="1px solid rgba(0,0,0,0.1)">
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Prêt à commencer ?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Créez votre compte gratuitement et commencez à générer vos QR codes.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<SignupIcon />}
              onClick={() => navigate('/signup')}
              sx={{
                py: 1.5,
                px: 5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: 8
                },
                transition: 'all 0.3s'
              }}
            >
              Créer un compte maintenant
            </Button>
          </Box>
        </Paper>

        {/* Copyright */}
        <Typography
          variant="body2"
          textAlign="center"
          sx={{ mt: 3, color: 'rgba(255,255,255,0.9)' }}
        >
          © 2025 Numihfrance. Tous droits réservés.
        </Typography>
      </Container>
    </Box>
  );
};

export default Home;
